import React, { useState, useEffect, useRef } from 'react';

interface OptimizedImageProps {
    src: string;
    alt: string;
    className?: string;
    width?: number | string;
    height?: number | string;
    lazy?: boolean;
    placeholder?: string;
    blurDataURL?: string;
    objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
    priority?: boolean;
    variant?: 'listing-grid' | 'listing-preview' | 'listing-detail' | 'thumbnail';
    fillMode?: 'cover' | 'contain' | 'smart';
    onLoad?: () => void;
    onError?: () => void;
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
    src,
    alt,
    className = '',
    width,
    height,
    lazy = true,
    placeholder = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxyYWRpYWxHcmFkaWVudCBpZD0iZ3JhZGllbnQiIGN4PSI1MCUiIGN5PSI1MCUiIHI9IjUwJSI+PHN0b3Agb2Zmc2V0PSIwJSIgc3R5bGU9InN0b3AtY29sb3I6I2YzZjNmMztzdG9wLW9wYWNpdHk6MSIvPjxzdG9wIG9mZnNldD0iMTAwJSIgc3R5bGU9InN0b3AtY29sb3I6I2UyZTJlMjtzdG9wLW9wYWNpdHk6MSIvPjwvcmFkaWFsR3JhZGllbnQ+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JhZGllbnQpIi8+PC9zdmc+',
    blurDataURL,
    objectFit = 'cover',
    priority = false,
    variant,
    fillMode,
    onLoad,
    onError
}) => {
    const [imageSrc, setImageSrc] = useState(blurDataURL || placeholder);
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [isInView, setIsInView] = useState(!lazy || priority);
    const imageRef = useRef<HTMLImageElement>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    useEffect(() => {
        // Eğer lazy loading aktifse, Intersection Observer ile görünürlüğü takip et
        if (lazy && !priority && imageRef.current) {
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            setIsInView(true);
                            observerRef.current?.unobserve(entry.target);
                        }
                    });
                },
                {
                    rootMargin: '200px', // 200px önceden yükle
                    threshold: 0.1
                }
            );

            observerRef.current.observe(imageRef.current);

            return () => {
                if (observerRef.current) {
                    observerRef.current.disconnect();
                }
            };
        }
    }, [lazy, priority]);

    useEffect(() => {
        if (isInView && src) {
            const img = new Image();

            img.src = src;
            img.onload = () => {
                setImageSrc(src);
                setIsLoaded(true);
                onLoad?.();
            };

            img.onerror = () => {
                setHasError(true);
                setImageSrc('/fallback-image.png'); // Fallback image
                onError?.();
            };
        }
    }, [src, isInView, onLoad, onError]);

    const handleImageError = () => {
        setHasError(true);
        setImageSrc('/fallback-image.png');
        onError?.();
    };

    // WebP formatına çevir (varsayılan olarak jpg/png'den webp'ye)

    // ImageKit Presets Config
    const PRESETS = {
        'listing-grid': ['w-600', 'q-auto', 'f-auto'],
        'listing-preview': ['w-800', 'q-80', 'f-auto'],
        'listing-detail': ['w-1200', 'q-90', 'f-auto'],
        'thumbnail': ['w-200', 'h-200', 'c-maintain_ratio', 'q-50', 'f-auto'],
    };

    // WebP formatına çevir (varsayılan olarak jpg/png'den webp'ye)
    const getOptimizedSrc = (url: string) => {
        if (hasError || !url || url.startsWith('data:') || url.includes('/fallback-image.png')) {
            return url;
        }

        // ImageKit Transformations
        if (url.includes('ik.imagekit.io')) {
            let params: string[] = [];

            // Use Preset if defined
            if (variant && PRESETS[variant]) {
                params = [...PRESETS[variant]];
            } else {
                if (width && typeof width === 'number') params.push(`w-${width}`);
                if (width === undefined && !variant) params.push('w-auto');
                params.push('q-auto');
                params.push('f-auto');
            }

            // Network Awareness (Save-Data) for Grid/Thumbnails
            // @ts-ignore
            const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
            const saveData = (navigator as any).connection?.saveData === true;
            const effectiveType = connection?.effectiveType;

            if (saveData || effectiveType === '2g' || effectiveType === '3g') {
                // Override quality for slow networks
                const qIndex = params.findIndex(p => p.startsWith('q-'));
                if (qIndex > -1) params[qIndex] = 'q-50';
                else params.push('q-50');

                params.push('bl-5');
            }

            // Check if already has params
            if (url.includes('?tr=')) {
                return url + ',' + params.join(',');
            } else {
                return url + '?tr=' + params.join(',');
            }
        }

        // Firebase Storage'dan gelen resimler için optimizasyon (Eski resimler için)
        if (url.includes('firebasestorage.googleapis.com')) {
            return url + '?alt=media';
        }

        return url;
    };

    const finalSrc = getOptimizedSrc(imageSrc);

    return (
        <div
            className={`relative overflow-hidden ${className}`}
            style={{
                width: width || '100%',
                ...(height ? { height } : {})
            }}
        >
            {/* Blur-up placeholder */}
            {!isLoaded && (
                <div className="absolute inset-0 shimmer-effect" />
            )}

            {/* Smart Blur Background Layer */}
            {fillMode === 'smart' && isLoaded && !hasError && (
                <>
                    <img
                        src={finalSrc}
                        alt=""
                        aria-hidden="true"
                        className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-50"
                    />
                    {/* Vignette & Overlay */}
                    <div className="absolute inset-0 bg-black/10 shadow-[inset_0_0_80px_rgba(0,0,0,0.3)] z-0" />
                </>
            )}

            {/* Ana resim */}
            <img
                ref={imageRef}
                src={finalSrc}
                alt={alt}
                loading={lazy && !priority ? 'lazy' : 'eager'}
                decoding="async"
                className={`transition-all duration-500 relative z-10 ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
                    } ${hasError ? 'grayscale filter' : ''}`}
                style={{
                    objectFit: fillMode === 'smart' ? 'contain' : objectFit,
                    width: '100%',
                    height: '100%',
                    backgroundColor: isLoaded ? 'transparent' : '#f3f3f3'
                }}
                onError={handleImageError}
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            />



            {/* Error indicator (Premium Fallback) */}
            {hasError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-brand-clay/5 p-4 z-20">
                    <svg className="w-8 h-8 text-brand-clay/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                </div>
            )}

        </div>
    );
};

export default OptimizedImage;
